import { useTranslation } from 'react-i18next'
import type { LanguageCode } from '@collector/shared'
import { useAuth } from '../lib/auth'
import { SUPPORTED_LANGUAGES, isSupportedLanguage } from '../lib/i18n'

/**
 * Flag emoji paired with each UI language. We display the flag of the
 * country most strongly associated with the language for Collector's
 * operator audience — English is rendered with the Union Jack (not US)
 * to match the product's European deployment. Kept out of the locale
 * files because flags are language-neutral labels.
 */
const LANGUAGE_FLAGS: Record<LanguageCode, string> = {
  en: '🇬🇧',
  uk: '🇺🇦',
  de: '🇩🇪',
  cs: '🇨🇿',
}

/**
 * Compact language dropdown shown in the app header. Selecting a language
 * updates i18n immediately, saves the preference to the authenticated
 * user's profile, and falls through to the locally-cached choice for
 * anonymous sessions (persisted via `i18next-browser-languagedetector`).
 */
export function LanguagePicker() {
  const { i18n, t } = useTranslation()
  const auth = useAuth()

  const active = i18n.resolvedLanguage ?? i18n.language
  const current: LanguageCode = isSupportedLanguage(active) ? active : 'en'

  return (
    <label className="flex items-center text-xs text-stone-600 dark:text-stone-400">
      <span className="sr-only">{t('nav.language')}</span>
      <select
        className="h-9 rounded-full border border-stone-200 bg-white/80 px-3 text-xs text-stone-700 shadow-sm backdrop-blur transition focus:border-emerald-700 focus:outline-none focus:ring-1 focus:ring-emerald-700 dark:border-stone-700 dark:bg-stone-900/80 dark:text-stone-200 dark:focus:border-emerald-500 dark:focus:ring-emerald-500"
        value={current}
        onChange={(e) => {
          const next = e.target.value
          if (!isSupportedLanguage(next)) return
          void auth.setLanguage(next)
        }}
      >
        {SUPPORTED_LANGUAGES.map((code) => (
          <option key={code} value={code}>
            {LANGUAGE_FLAGS[code]} {t(`languages.${code}`)}
          </option>
        ))}
      </select>
    </label>
  )
}
